import React,{useState,useEffect, PropsWithChildren} from "react";
import {IonContent,IonToolbar,IonHeader,IonButtons, IonSearchbar, IonAvatar, IonButton, IonList, IonItem, IonLabel, IonIcon} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import countries from "../../countries.json";

import './CountryPhoneSelection.css';
import { closeOutline } from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

interface MyModalProps extends PropsWithChildren{
    closeAction():any,
    setCallingCode(callingCode:string):any,
    setCountryName(countryName:string):any,
    phone?:boolean,
    country?:boolean
}

interface Country {

    flag:string;
    name:string;
    alpha2Code:string;
    alpha3Code: string;
    callingCodes:string[];

}

const CountryPhoneSelection: React.FC<MyModalProps> = (props) => {

    const { t, i18n } = useTranslation();
    const [searchText, setSearchText] = useState('');
   
    const [countryArray,setCountryArray] = useState<Country[]>([])

    const setCallingCodeAndClose = (callingCode:string) =>{

        props.setCallingCode(callingCode);
        props.closeAction();

    }

    const setCountryCodeAndClose = (countryName:string) =>{

        props.setCountryName(countryName);
        props.closeAction();

    }

    const filterCountryArray = (searchQuery:string)=>{

        setSearchText(searchQuery)
       
        let tempSearchResult = countries.filter(ele => 
            ele.alpha2Code.includes(searchQuery) 
            || ele.alpha3Code.includes(searchQuery)
            || ele.callingCodes[0].includes(searchQuery)
            || ele.name.includes(searchQuery)
            || ele.name.toLocaleLowerCase().includes(searchQuery)
            
        )
        setCountryArray([...tempSearchResult])

    }

    useEffect(() => {

        let tempCountryArray:Country[] = [];

        countries.map(country=>{

            tempCountryArray.push(country)

        })

        setCountryArray([...tempCountryArray])
        
    },[])
     
    const handleScrollStart = async()=>{

        if (Capacitor.getPlatform()==='ios' || Capacitor.getPlatform()==='android'){
            Keyboard.hide();
        }
        
    }
    
    return(
        <>
            <IonHeader className="ion-no-border ">
                {/* Header with close button and title */}
                <IonToolbar className="bg-white dark:bg-slate-900">
                    <div className="flex items-center justify-between px-4 py-2">
                        <IonButton 
                            fill="clear" 
                            onClick={() => {props.closeAction()}}
                            className="close-button"
                        >
                            <IonIcon 
                                slot="icon-only" 
                                icon={closeOutline} 
                                className="w-6 h-6 text-gray-600 dark:text-gray-300" 
                            />
                        </IonButton>
                        
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white font-['Aeonik'] flex-1 text-center mr-12">
                            {props.phone ? t('country-selection.SELECT_COUNTRY_CODE') : t('country-selection.SELECT_COUNTRY')}
                        </h2>
                    </div>
                </IonToolbar> 
                
                {/* Enhanced Search Bar */}
                <IonToolbar className="bg-gray-50 dark:bg-slate-800 pb-2">
                    <div className="px-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <IonSearchbar 
                                className="search-bar-enhanced"
                                value={searchText} 
                                onIonInput={(e:any) => filterCountryArray(e.detail.value!)}
                                placeholder={t('country-selection.SEARCH_PLACEHOLDER') as string}
                                showClearButton="focus"
                            />
                        </div>
                    </div>
                </IonToolbar>       
            </IonHeader>
               
            <IonContent 
                scrollEvents={true}
                onIonScrollStart={handleScrollStart}
                className="bg-gray-50 dark:bg-slate-900"
            >
                {/* Results count */}
                {searchText && (
                    <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
                        <p className="text-sm text-blue-700 dark:text-blue-300 font-['Aeonik']">
                            {countryArray.length} {countryArray.length === 1 ? t('country-selection.RESULT_SINGULAR') : t('country-selection.RESULT_PLURAL')} found
                        </p>
                    </div>
                )}

                <IonList className="bg-transparent" style={{ height: 'auto', minHeight: 'auto' }}>
                    {countryArray.length > 0 ? (
                        countryArray.map((el, index) => (
                            <IonItem
                                lines="none"
                                key={el.alpha2Code.toString()}
                                onClick={() => props.phone ? setCallingCodeAndClose(el.callingCodes[0]) : setCountryCodeAndClose(el.alpha2Code)}
                                className={`
                                    country-item hover:bg-blue-50 dark:hover:bg-slate-800 
                                    transition-colors duration-200 cursor-pointer
                                    ${index === 0 ? 'mt-2' : ''}
                                `}
                                button
                            >
                                {/* Flag with better styling */}
                                <div slot="start" className="flag-container">
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center shadow-sm">
                                        <img 
                                            src={`https://flagcdn.com/w40/${el.alpha2Code.toLowerCase()}.png`}
                                            srcSet={`https://flagcdn.com/w80/${el.alpha2Code.toLowerCase()}.png 2x`}
                                            alt={`${el.name} flag`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>

                                {/* Country info with better typography */}
                                <div className="flex flex-col py-3 ml-3 flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-base font-medium text-gray-900 dark:text-white">
                                            {el.name}
                                        </span>
                                        {props.phone && (
                                            <span className="text-sm font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                                                +{el.callingCodes[0]}
                                            </span>
                                        )}
                                    </div>
                                </div>

                            </IonItem>
                        ))
                    ) : searchText ? (
                        // No results state
                        <div className="flex flex-col items-center justify-center py-12 px-6">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Aeonik'] mb-2">
                                {t('country-selection.NO_RESULTS_FOUND')}
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-center font-['Aeonik']">
                                {t('country-selection.TRY_ADJUSTING_SEARCH')}
                            </p>
                        </div>
                    ) : null}
                </IonList>
            </IonContent>
        </>
    )
}

export default CountryPhoneSelection